import { useEffect, useRef, useState } from 'react';
import { ConsentConfig, FALLBACK_CONSENT_CONFIG, getConsentConfig } from '@/app/utilities/apis';

// One request per page load — every lead form shares the same config.
let configRequest: Promise<ConsentConfig> | null = null;

/**
 * DPDP consent checkbox state for a lead form. Until the config arrives (or if
 * it fails) the checkbox is shown unticked, so a form can never be submitted
 * without consent. `canSubmit` is false while a displayed checkbox is unticked.
 * Pass `enabled = false` to defer the config request until a form is opened.
 */
export function useLeadConsent(enabled = true) {
    const [config, setConfig] = useState<ConsentConfig>(FALLBACK_CONSENT_CONFIG);
    const [checked, setCheckedState] = useState(FALLBACK_CONSENT_CONFIG.default_checked);
    const touchedRef = useRef(false);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        if (!configRequest) configRequest = getConsentConfig();
        configRequest.then((resolved) => {
            if (cancelled) return;
            setConfig(resolved);
            // Don't override a choice the user already made.
            if (!touchedRef.current) setCheckedState(resolved.default_checked);
        });
        return () => { cancelled = true; };
    }, [enabled]);

    const setChecked = (value: boolean) => {
        touchedRef.current = true;
        setCheckedState(value);
    };

    return {
        config,
        checked,
        setChecked,
        canSubmit: !config.is_displayed || checked,
    };
}

export type LeadConsentState = ReturnType<typeof useLeadConsent>;
